import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { dishService, specialNameService } from '../utils/api';
import { matchesIgnoringAccents, containsIgnoringAccents } from '../utils/stringUtils';
import { useDishes } from '../hooks/useDishes';
import { useSpecialNames } from '../hooks/useSpecialNames';

/**
 * Reusable component for deleting a dish or special name
 * Handles showing usage information, replacement selection, and confirmation
 * Auto-focuses the replacement input field when opened for better UX
 */
const DeleteDishModal = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  itemId, 
  itemType, // 'dish' or 'specialName'
  schoolId,
  language = 'en',
  defaultLanguage = 'en' // Add defaultLanguage prop with 'en' as fallback
}) => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef(null); // Reference to the input field
  
  // Component state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [itemUsageInfo, setItemUsageInfo] = useState(null);
  const [selectedReplacementId, setSelectedReplacementId] = useState(null);
  const [replacementSearchQuery, setReplacementSearchQuery] = useState('');
  // Local state for immediate input feedback
  const [inputValue, setInputValue] = useState('');
  // New states for improved UI feedback
  const [validSelectionMade, setValidSelectionMade] = useState(false);
  const [showDropdown, setShowDropdown] = useState(true);
  const [inputFocused, setInputFocused] = useState(false);
  const [inputErrorShown, setInputErrorShown] = useState(false);

  // Use React Query hooks to get cached data (like MenuEditor does)
  const { data: allDishes = [], isLoading: dishesLoading } = useDishes(schoolId);
  const { data: allSpecialNames = [], isLoading: specialNamesLoading } = useSpecialNames(schoolId);

  // Get all items based on type from React Query cache
  const allItems = itemType === 'dish' ? allDishes : allSpecialNames;
  const isItemsLoading = itemType === 'dish' ? dishesLoading : specialNamesLoading;

  // Helper function to prioritize translations (like MenuEditor's findAvailableTranslation)
  const getSortedTranslations = useCallback((translations) => {
    if (!translations || translations.length === 0) return null;
    
    // First try to find a translation in the current language
    const currentLangTranslation = translations.find(t => 
      t.language_code === i18n.language && t.name
    );
    
    if (currentLangTranslation) return {
      translation: currentLangTranslation,
      language: i18n.language,
      isFallback: false
    };
    
    // Next, try to find a translation in the school's default language
    if (defaultLanguage) {
      const defaultLangTranslation = translations.find(t => 
        t.language_code === defaultLanguage && t.name
      );
      
      if (defaultLangTranslation) return {
        translation: defaultLangTranslation,
        language: defaultLanguage,
        isFallback: true
      };
    }
    
    // If school's default language translation is not available, try English as a common fallback
    const englishTranslation = translations.find(t => 
      t.language_code === 'en' && t.name
    );
    
    if (englishTranslation) return {
      translation: englishTranslation,
      language: 'en',
      isFallback: true
    };
    
    // If no prioritized language is found, just take the first one with a name
    const firstAvailable = translations.find(t => t.name);
    if (firstAvailable) return {
      translation: firstAvailable,
      language: firstAvailable.language_code,
      isFallback: true
    };
    
    return null;
  }, [i18n.language, defaultLanguage]);
  
  // Memoize getDisplayName to prevent it from changing on every render
  const getDisplayName = useCallback((item) => {
    // Safety check - if item is undefined or null
    if (!item) {
      return { 
        name: t('admin.noTranslation'), 
        isFallback: true 
      };
    }
    
    // If we have a name property directly (current language translation exists)
    if (item.name) {
      return { name: item.name, isFallback: false };
    }
    
    // Cache to store the result once we find it
    let result = null;
    
    // Check if translations array exists and has entries
    if (!result && item.translations && Array.isArray(item.translations) && item.translations.length > 0) {
      result = getSortedTranslations(item.translations);
    }
    
    // Check all_translations property (which may be used in the search results)
    if (!result && item.all_translations && Array.isArray(item.all_translations) && item.all_translations.length > 0) {
      result = getSortedTranslations(item.all_translations);
    }
    
    // If we found a valid translation, return it
    if (result) {
      return {
        name: result.translation.name,
        isFallback: result.isFallback,
        language: result.language
      };
    }
    
    // If item has default_name property
    if (item.default_name) {
      return { 
        name: item.default_name, 
        isFallback: true 
      };
    }
    
    // Fallback to a generic label
    return { 
      name: `${itemType === 'dish' ? 'Dish' : 'Special Name'} #${item.id}`, 
      isFallback: true 
    };
  }, [t, itemType, getSortedTranslations]);

  // Filter replacement items client-side using containsIgnoringAccents (like MenuEditor)
  // Now using useMemo since getDisplayName is defined above
  const replacementItems = useMemo(() => {
    return allItems
      .filter(item => item.id !== itemId) // Exclude current item
      .filter(item => {
        if (!replacementSearchQuery.trim()) return true;
        
        // Get item name for searching
        const itemName = getDisplayName(item).name;
        return containsIgnoringAccents(itemName, replacementSearchQuery);
      });
  }, [allItems, itemId, replacementSearchQuery, getDisplayName]);
  
  // Find selected item by ID
  const getSelectedItem = useCallback(() => {
    if (!selectedReplacementId) return null;
    return replacementItems.find(item => item.id === selectedReplacementId) || null;
  }, [selectedReplacementId, replacementItems]);
  
  // Validate if current input matches a replacement item
  const validateCurrentInput = useCallback(() => {
    if (!inputValue.trim() || !replacementItems.length) {
      setSelectedReplacementId(null);
      setValidSelectionMade(false);
      return false;
    }
    
    // Check if current input exactly matches any replacement item
    const matchingItems = replacementItems.filter(item => {
      const displayInfo = getDisplayName(item);
      return matchesIgnoringAccents(displayInfo.name, inputValue.trim());
    });
    
    if (matchingItems.length > 0) {
      // Select the first matching item
      const matchedItem = matchingItems[0];
      setSelectedReplacementId(matchedItem.id);
      
      // Update input to match the exact case of the item for consistency
      const displayInfo = getDisplayName(matchedItem);
      if (inputValue !== displayInfo.name) {
        setInputValue(displayInfo.name);
      }
      
      setValidSelectionMade(true);
      return true;
    } else {
      // Input doesn't match any item - clear any previously selected item
      setSelectedReplacementId(null);
      setValidSelectionMade(false);
      // Only show dropdown if input is focused
      setShowDropdown(inputFocused);
      return false;
    }
  }, [inputValue, replacementItems, inputFocused, getDisplayName]);
  
  // Handle selecting an item from the dropdown
  const handleSelectItem = useCallback((item) => {
    if (!item) return;
    
    // Get consistent display name
    const displayInfo = getDisplayName(item);
    
    // Update the state
    setSelectedReplacementId(item.id);
    setInputValue(displayInfo.name);
    setValidSelectionMade(true);
    
    // Hide dropdown immediately since we're removing focus
    setShowDropdown(false);
    setInputFocused(false);
    
    // Remove focus from the input field after a slight delay
    // to ensure the value is updated properly
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }, 50);
  }, [getDisplayName]);
  
  // Check item usage when modal opens
  useEffect(() => {
    const checkItemUsage = async () => {
      if (!isOpen || !itemId) return;
      
      try {
        setLoading(true);
        setError(null);
        setValidSelectionMade(false);
        setShowDropdown(true);
        
        // Check if the item is used in any menus
        let usageInfo;
        if (itemType === 'dish') {
          usageInfo = await dishService.checkDishUsage(itemId);
        } else {
          usageInfo = await specialNameService.checkSpecialNameUsage(itemId);
        }
        
        setItemUsageInfo(usageInfo);
        
        // No need to load replacement items via API - they come from React Query cache
        
        // Reset the input value and search query
        setInputValue('');
        setReplacementSearchQuery('');
        setSelectedReplacementId(null);
        
        setLoading(false);
        
        // Focus the input field after the modal has loaded
        if (usageInfo.isUsed && inputRef.current) {
          setTimeout(() => {
            inputRef.current.focus();
          }, 100); // Short delay to ensure DOM is ready
        }
      } catch (err) {
        console.error('Failed to check item usage:', err);
        setError('Failed to check if this item can be deleted. Please try again later.');
        setLoading(false);
      }
    };
    
    checkItemUsage();
  }, [isOpen, itemId, itemType]); // Removed searchReplacementItems dependency
  
  // Dedicated effect to focus input when modal opens and item requires replacement
  useEffect(() => {
    if (isOpen && itemUsageInfo?.isUsed && inputRef.current && !loading) {
      // Focus the input field with a small delay to ensure rendering is complete
      const focusTimer = setTimeout(() => {
        inputRef.current.focus();
      }, 200);
      
      return () => clearTimeout(focusTimer);
    }
  }, [isOpen, itemUsageInfo, loading]);
  
  // Handle input focus
  const handleInputFocus = () => {
    setInputFocused(true);
    setShowDropdown(true);
  };
  
  // Handle input blur
  const handleInputBlur = () => {
    // Don't immediately set inputFocused to false
    // as this could interfere with item selection
    
    // Hide dropdown after a short delay (to allow clicks on dropdown items)
    setTimeout(() => {
      if (!document.activeElement || document.activeElement !== inputRef.current) {
        setInputFocused(false);
        setShowDropdown(false);
        
        // Validate the input after setting focus state
        validateCurrentInput();
      }
    }, 200); // Increased delay to ensure click events complete first
  };
  
  // Handle direct input change
  const handleReplacementInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Show dropdown when input changes
    setShowDropdown(true);
    
    // When input changes, immediately check if it still matches the selected item
    if (selectedReplacementId) {
      const selectedItem = replacementItems.find(item => item.id === selectedReplacementId);
      if (selectedItem) {
        const displayInfo = getDisplayName(selectedItem);
        
        // If input no longer matches the selected item's name, invalidate the selection
        if (!matchesIgnoringAccents(displayInfo.name, newValue)) {
          setSelectedReplacementId(null);
          setValidSelectionMade(false);
        }
      }
    }
  };
  
  // Update search query when input changes (for client-side filtering)
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (isOpen && itemUsageInfo?.isUsed) {
        setReplacementSearchQuery(inputValue); // This triggers client-side filtering
      }
    }, 300); // 300ms delay

    return () => clearTimeout(delaySearch);
  }, [inputValue, isOpen, itemUsageInfo]); // Removed searchReplacementItems dependency
  
  // Separate effect for validation after search completes
  useEffect(() => {
    if (isOpen && itemUsageInfo?.isUsed) {
      validateCurrentInput();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemUsageInfo, replacementItems]);
  
  // Update selected item display value if selection changes
  useEffect(() => {
    const selectedItem = getSelectedItem();
    if (selectedItem && validSelectionMade) {
      const displayInfo = getDisplayName(selectedItem);
      if (inputValue !== displayInfo.name) {
        setInputValue(displayInfo.name);
      }
    }
  }, [selectedReplacementId, getSelectedItem, getDisplayName, validSelectionMade, inputValue]);
  
  // Delete the item
  const handleDeleteItem = async () => {
    if (!itemId) return;
    
    // Extra validation before attempting to delete
    if (itemUsageInfo?.isUsed) {
      const isValid = validateCurrentInput();
      if (!isValid) {
        setInputErrorShown(true);
        setTimeout(() => setInputErrorShown(false), 3000); // Hide after 3 seconds
        return;
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      if (itemUsageInfo?.isUsed && selectedReplacementId) {
        // Delete with replacement
        if (itemType === 'dish') {
          await dishService.replaceDishAndDelete(itemId, selectedReplacementId);
        } else {
          await specialNameService.replaceSpecialNameAndDelete(itemId, selectedReplacementId);
        }
      } else if (!itemUsageInfo?.isUsed) {
        // Simple delete
        if (itemType === 'dish') {
          await dishService.deleteDish(itemId);
        } else {
          await specialNameService.deleteSpecialName(itemId);
        }
      } else {
        // Item is used but no replacement selected
        throw new Error('A replacement must be selected to delete this item.');
      }
      
      // Close modal and notify parent of success
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
      setError(err.message || 'Failed to delete item. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Get the formatted usage message to ensure count interpolation works
  const getUsageMessage = useCallback(() => {
    if (!itemUsageInfo || !itemUsageInfo.isUsed) {
      return '';
    }
    
    // Extract the count value based on item type
    let count = 0;
    if (itemType === 'dish') {
      count = itemUsageInfo.usageDetails.menuItemsCount || 0;
    } else {
      count = itemUsageInfo.usageDetails.specialNameCount || 0;
    }
    
    // Ensure count is a number for i18next
    count = Number(count);
    
    // Create the message using direct string interpolation as a fallback
    // in case the i18next interpolation isn't working
    let key = itemType === 'dish' ? 'modal.dishInUse' : 'modal.specialNameInUse';
    let translated = t(key, { count });
    
    // If the translation still shows {count}, manually replace it
    if (translated.includes('{count}')) {
      let rawMessage = i18n.getResource(i18n.language, 'translation', key);
      translated = rawMessage?.replace('{count}', count) || translated;
    }
    
    return translated;
  }, [itemUsageInfo, itemType, t, i18n]);
  
  if (!isOpen) return null;
  
  // Show loading state until the initial data is fully loaded
  // This prevents flickering by ensuring we only show the final state
  const isInitialLoading = loading || !itemUsageInfo;
  
  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div 
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                  {t('modal.confirmDelete')}
                </h3>
                <div className="mt-2 modal-content-container" style={{ minHeight: '200px', height: 'auto' }}>
                  {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {error}
                    </div>
                  )}
                  
                  {isInitialLoading ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  ) : itemUsageInfo.isUsed ? (
                    <div>
                      {/* Item is used in menus - Using the formatted message function */}
                      <p className="text-sm text-gray-500">
                        {getUsageMessage()}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        {itemType === 'dish' 
                          ? t('modal.selectReplacementDish') 
                          : t('modal.selectReplacementSpecialName')}
                      </p>
                      
                      {/* Replacement search input */}
                      <div className="mt-3">
                        <input
                          ref={inputRef}
                          type="text"
                          placeholder={t('admin.searchReplacement')}
                          className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onChange={handleReplacementInputChange}
                          onFocus={handleInputFocus}
                          onBlur={handleInputBlur}
                          value={inputValue}
                        />
                      </div>
                      
                      {/* Valid selection confirmation (new) */}
                      {validSelectionMade && !inputFocused && (
                        <div className="flex items-center mt-2 text-sm text-green-600">
                          <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          <span>{t('modal.validReplacementSelected')}</span>
                        </div>
                      )}
                      
                      {/* Fixed height container for replacement items */}
                      <div className="relative" style={{ height: '150px' }}>
                        {/* Replacement search results - fixed height with overflow */}
                        {showDropdown && (
                          <div className="absolute w-full mt-2 border rounded" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {isItemsLoading ? (
                              <div className="flex justify-center py-4">
                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                              </div>
                            ) : replacementItems.length > 0 ? (
                              <ul className="divide-y">
                                {replacementItems.map(item => {
                                  const displayInfo = getDisplayName(item);
                                  return (
                                    <li key={item.id} className="p-2">
                                      <button
                                        className={`w-full text-left px-2 py-1.5 rounded ${selectedReplacementId === item.id ? 'bg-blue-100' : 'hover:bg-gray-100'}`}
                                        type="button"
                                        onMouseDown={(e) => {
                                          // Prevent input blur before click is processed
                                          e.preventDefault();
                                          
                                          // Call handleSelectItem directly to ensure it runs
                                          // This prevents race conditions with blur events
                                          handleSelectItem(item);
                                        }}
                                        onClick={() => handleSelectItem(item)}
                                      >
                                        {displayInfo.isFallback ? (
                                          <div>
                                            <span className="text-gray-500 italic">{displayInfo.name}</span>
                                            {displayInfo.language && (
                                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded">
                                                {displayInfo.language}
                                              </span>
                                            )}
                                          </div>
                                        ) : (
                                          displayInfo.name
                                        )}
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <p className="text-gray-500 p-3 text-sm text-center">
                                {replacementSearchQuery ? t('No results match your search') : t('Type to search for replacement')}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Error message for missing replacement */}
                      {!validSelectionMade && (!showDropdown || inputErrorShown) && (
                        <p className="text-sm text-red-500 mt-2 animate-pulse">
                          {t('modal.replacementRequired')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {itemType === 'dish' 
                        ? t('modal.confirmDeleteDish') 
                        : t('modal.confirmDeleteSpecialName')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              type="button" 
              onClick={handleDeleteItem}
              disabled={isInitialLoading || (itemUsageInfo?.isUsed && !selectedReplacementId) || loading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {loading 
                ? t('admin.deleting') 
                : (itemUsageInfo?.isUsed ? t('modal.deleteAndReplace') : t('modal.deletePermanently'))}
            </button>
            <button 
              type="button" 
              onClick={onClose}
              disabled={isInitialLoading || loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteDishModal; 